import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { timesheetsAPI, usersAPI, jobsAPI, customEntriesAPI } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { formatDate, formatDateRange } from '../utils/dateUtils';
import { Loader2, CheckCircle2, Clock, FileText, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { className: 'badge-draft', label: 'Draft' },
    submitted: { className: 'badge-pending', label: 'Pending' },
    approved: { className: 'badge-approved', label: 'Approved' },
    rejected: { className: 'badge-rejected', label: 'Rejected' },
  };

  const config = statusConfig[status] || statusConfig.draft;
  return <span className={config.className}>{config.label}</span>;
};

const ApprovalsPage = () => {
  const [timesheets, setTimesheets] = useState([]);
  const [customEntries, setCustomEntries] = useState([]);
  const [users, setUsers] = useState({});
  const [jobs, setJobs] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedCustomIds, setSelectedCustomIds] = useState(new Set());
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tsRes, usersRes, jobsRes, ceRes] = await Promise.all([
        timesheetsAPI.getPending(),
        usersAPI.getAll(),
        jobsAPI.getAll(),
        customEntriesAPI.getAll()
      ]);
      
      setTimesheets(tsRes.data);
      
      // Filter custom entries to only show submitted ones
      const pendingCustomEntries = ceRes.data.filter(ce => ce.status === 'submitted');
      setCustomEntries(pendingCustomEntries);
      
      // Create lookup maps
      const usersMap = {};
      usersRes.data.forEach(u => { usersMap[u.id] = u; });
      setUsers(usersMap);
      
      const jobsMap = {};
      jobsRes.data.forEach(j => { jobsMap[j.id] = j; });
      setJobs(jobsMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === timesheets.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(timesheets.map(ts => ts.id)));
    }
  };

  const handleBatchApprove = async () => {
    if (selectedIds.size === 0) return;
    
    setApproving(true);
    try {
      const response = await timesheetsAPI.batchApprove(Array.from(selectedIds));
      const { approved_count, failed_count } = response.data;
      
      if (approved_count > 0) {
        toast.success(`Successfully approved ${approved_count} timesheet${approved_count > 1 ? 's' : ''}`);
      }
      if (failed_count > 0) {
        toast.error(`Failed to approve ${failed_count} timesheet${failed_count > 1 ? 's' : ''}`);
      }
      
      setSelectedIds(new Set());
      await fetchData();
    } catch (error) {
      console.error('Batch approval failed:', error);
      toast.error('Failed to approve timesheets');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#194eb2]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Pending Approvals</h1>
          <p className="mt-1 text-slate-500">
            Review and approve submitted timesheets
          </p>
        </div>

        {/* Stats and Batch Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-4">
            <Clock className="w-8 h-8 text-amber-600" />
            <div>
              <p className="text-2xl font-bold text-amber-700">{timesheets.length + customEntries.length}</p>
              <p className="text-sm text-amber-600">
                {timesheets.length} timesheet{timesheets.length !== 1 ? 's' : ''}, {customEntries.length} custom entr{customEntries.length !== 1 ? 'ies' : 'y'} awaiting approval
              </p>
            </div>
          </div>
          
          {timesheets.length > 0 && (
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                data-testid="select-all-btn"
              >
                {selectedIds.size === timesheets.length ? (
                  <CheckSquare className="w-4 h-4 text-[#194eb2]" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                {selectedIds.size === timesheets.length ? 'Deselect All' : 'Select All'}
              </button>
              
              {selectedIds.size > 0 && (
                <button
                  onClick={handleBatchApprove}
                  disabled={approving}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                  data-testid="batch-approve-btn"
                >
                  {approving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  Approve Selected ({selectedIds.size})
                </button>
              )}
            </div>
          )}
        </div>

        {/* Approvals List */}
        {timesheets.length === 0 && customEntries.length === 0 ? (
          <div className="card-base">
            <div className="card-body text-center py-12">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">All caught up!</h3>
              <p className="text-slate-500">There are no items pending approval.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Regular Timesheets */}
            {timesheets.map((ts) => {
              const user = users[ts.user_id];
              const job = jobs[ts.job_id];
              const isSelected = selectedIds.has(ts.id);
              
              return (
                <div key={ts.id} className={`card-base transition-colors ${isSelected ? 'ring-2 ring-[#194eb2] ring-offset-2' : ''}`}>
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1 flex items-start gap-4">
                        {/* Selection Checkbox */}
                        <button
                          onClick={() => toggleSelection(ts.id)}
                          className="mt-1 flex-shrink-0"
                          data-testid={`select-${ts.id}`}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-[#194eb2]" />
                          ) : (
                            <Square className="w-5 h-5 text-slate-400 hover:text-slate-600" />
                          )}
                        </button>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-[#194eb2] rounded-full flex items-center justify-center">
                              <span className="text-white font-medium">
                                {user?.name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{user?.name || 'Unknown'}</p>
                              <p className="text-sm text-slate-500">{user?.email}</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase">Period</p>
                              <p className="text-sm text-slate-900">{formatDateRange(ts.period_start, ts.period_end)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase">Project</p>
                              <p className="text-sm text-slate-900">{job?.job_number || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase">Days</p>
                              <p className="text-sm font-mono text-slate-900">{ts.total_days}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase">Hours</p>
                              <p className="text-sm font-mono text-slate-900">{ts.total_hours}</p>
                            </div>
                          </div>
                          
                          {ts.entries && ts.entries.length > 0 && (
                            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                              <p className="text-xs font-medium text-slate-500 uppercase mb-2">Entries Summary</p>
                              <div className="space-y-1">
                                {ts.entries.slice(0, 3).map((entry, idx) => (
                                  <p key={idx} className="text-sm text-slate-600">
                                    {entry.date}: {entry.description || 'No description'} ({entry.quantity} {entry.unit_type})
                                  </p>
                                ))}
                                {ts.entries.length > 3 && (
                                  <p className="text-sm text-slate-400">
                                    +{ts.entries.length - 3} more entries
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <StatusBadge status={ts.status} />
                        <button
                          onClick={async () => {
                            try {
                              await timesheetsAPI.approve(ts.id);
                              toast.success('Timesheet approved');
                              fetchData();
                            } catch (error) {
                              toast.error('Failed to approve timesheet');
                            }
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
                          data-testid={`approve-ts-${ts.id}`}
                        >
                          Approve
                        </button>
                        <button
                          onClick={async () => {
                            const comment = prompt('Rejection reason:');
                            if (comment === null) return; // User cancelled
                            if (!comment.trim()) {
                              toast.error('Please provide a rejection reason');
                              return;
                            }
                            try {
                              await timesheetsAPI.reject(ts.id, comment);
                              toast.success('Timesheet rejected');
                              fetchData();
                            } catch (error) {
                              toast.error('Failed to reject timesheet');
                            }
                          }}
                          className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                          data-testid={`reject-ts-${ts.id}`}
                        >
                          Reject
                        </button>
                        <Link
                          to={`/timesheets/${ts.id}`}
                          className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 transition-colors"
                          data-testid={`review-${ts.id}`}
                        >
                          Review
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Custom Entries */}
            {customEntries.map((ce) => {
              const user = users[ce.user_id];
              const job = jobs[ce.job_id];
              
              return (
                <div key={`ce-${ce.id}`} className="card-base border-l-4 border-l-amber-400">
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1 flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-medium">
                                {user?.name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-slate-900">{user?.name || 'Unknown'}</p>
                                <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">
                                  Custom Entry
                                </span>
                              </div>
                              <p className="text-sm text-slate-500">{user?.email}</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase">Period</p>
                              <p className="text-sm text-slate-900">{formatDateRange(ce.period_start, ce.period_end)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase">Project</p>
                              <p className="text-sm text-slate-900">{job?.job_number || '-'}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase">Value</p>
                              <p className="text-sm font-mono text-slate-900 font-semibold">${ce.total_value?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase">Entries</p>
                              <p className="text-sm font-mono text-slate-900">{ce.entries?.length || 0}</p>
                            </div>
                          </div>
                          
                          {ce.entries && ce.entries.length > 0 && (
                            <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                              <p className="text-xs font-medium text-slate-500 uppercase mb-2">Entries Summary</p>
                              <div className="space-y-1">
                                {ce.entries.slice(0, 3).map((entry, idx) => (
                                  <p key={idx} className="text-sm text-slate-600">
                                    {entry.description || 'No description'} - Qty: {entry.quantity} × ${entry.value} = ${(entry.quantity * entry.value).toFixed(2)}
                                  </p>
                                ))}
                                {ce.entries.length > 3 && (
                                  <p className="text-sm text-slate-400">
                                    +{ce.entries.length - 3} more entries
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <StatusBadge status={ce.status} />
                        <button
                          onClick={async () => {
                            try {
                              await customEntriesAPI.approve(ce.id);
                              toast.success('Custom entry approved');
                              fetchData();
                            } catch (error) {
                              toast.error('Failed to approve custom entry');
                            }
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
                          data-testid={`approve-ce-${ce.id}`}
                        >
                          Approve
                        </button>
                        <button
                          onClick={async () => {
                            const comment = prompt('Rejection reason (optional):');
                            try {
                              await customEntriesAPI.reject(ce.id, comment || '');
                              toast.success('Custom entry rejected');
                              fetchData();
                            } catch (error) {
                              toast.error('Failed to reject custom entry');
                            }
                          }}
                          className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                          data-testid={`reject-ce-${ce.id}`}
                        >
                          Reject
                        </button>
                        <Link
                          to={`/custom-entry/${ce.id}`}
                          className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 transition-colors"
                          data-testid={`review-ce-${ce.id}`}
                        >
                          Review
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ApprovalsPage;
